import React, { ReactNode } from "react";
import { Button } from "./ui/button";
import * as Sentry from "@sentry/react";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardCopyIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { track } from "@/utils/events/tracker";
import { logAxiosError } from "@/utils/utils";
import { cn } from "@/lib/utils";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  showErrorDetails?: boolean;
  translationError?: boolean;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    // Define a state variable to track whether there is an error or not
    this.state = {
      hasError: false,
      showErrorDetails: false,
      translationError: false,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      showErrorDetails: false,
      translationError: false,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // You can use your own error logging service here
    logAxiosError("App crash error", error);
    console.error(errorInfo);
    console.error({ error, errorInfo });
    console.trace();
    Sentry.captureException(error);

    const errorString = error?.toString();

    track("APP_CRASH", {
      error: errorString,
      stack: errorInfo?.componentStack,
    });

    const translationError =
      errorString?.includes(
        "NotFoundError: Failed to execute 'insertBefore' on 'Node'"
      ) ||
      errorString?.includes(
        "NotFoundError: Failed to execute 'removeChild' on 'Node'"
      );

    this.setState({ error, errorInfo, translationError });
  }

  handleRetry = () => {
    if (window.location.pathname.includes("/editor")) {
      // If the error occurred in the /editor context, reload the current page
      this.setState({ hasError: false }, () => {
        window.location.reload();
      });
    } else {
      // Route to '/'
      this.setState({ hasError: false }, () => {
        window.location.replace("/");
      });
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-screen bg-secondary-100 flex flex-col items-center gap-4 justify-center h-screen">
          <TriangleAlertIcon className="w-20 h-20 stroke-white mb-[-0.5rem]" />
          <h2 className="text-2xl font-semibold pt-2">
            Oops, there was an error!
          </h2>
          <h2
            className={cn(
              "text-md pb-2",
              !this.state.translationError && "hidden"
            )}
          >
            This error is likely due to the auto-translation feature in your
            browser. We suggest disabling it.
          </h2>
          <Button onClick={this.handleRetry} variant="primary" className="w-36">
            {window.location.pathname.includes("/editor")
              ? "Reopen Canvas"
              : "Go Home"}
          </Button>

          {this.state.error && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
              className="gap-4"
            >
              <Button
                variant="outline"
                style={{
                  width: 200,
                }}
                className="flex flex-row gap-1"
                onClick={() => {
                  this.setState({
                    showErrorDetails: !this.state.showErrorDetails,
                  });
                }}
              >
                <span>
                  {this.state.showErrorDetails ? (
                    <ChevronUpIcon />
                  ) : (
                    <ChevronDownIcon />
                  )}
                </span>
                Show Error Details
              </Button>
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  opacity: this.state.showErrorDetails ? 1 : 0,
                }}
                className="flex flex-row gap-2"
              >
                <ClipboardCopyIcon
                  className="cursor-pointer text-white hover:text-secondary-900 w-4 h-4 shrink-0"
                  onClick={() => {
                    // @ts-expect-error TS2532
                    let errorMessage = this.state.error.toString();

                    if (this.state.errorInfo?.componentStack) {
                      errorMessage += this.state.errorInfo?.componentStack;
                    }

                    navigator.clipboard.writeText(errorMessage);
                  }}
                />
                <div className="text-2xs overflow-y-auto max-h-[300px]">
                  {this.state.error.toString()}
                  {/* <br /> */}
                  {this.state.errorInfo?.componentStack}
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    // Return children components in case of no error
    return this.props.children;
  }
}

export default ErrorBoundary;
